// report.service.ts
import { Injectable } from '@angular/core';
import { ReportHttpService } from "./https/report-http.service";
import { map, Observable } from "rxjs";
import { IResultReportm, ResultReportm } from "../../models/reports/report.model";
import { IResultClosedRa, ResultClosedRa } from '../../models/reports/closed-ra.model';
import { IResultClosedGap, ResultClosedGap } from '../../models/reports/closed-gap.model';
import { IResultBonusResumen, ResultBonusResumen } from "../../models/reports/bonus-resumen.model";
import { IResultBonusDetail, ResultBonusDetail } from "../../models/reports/bonus-detail.model";
import { IResultTsm, ResultTsm } from "../../models/reports/tsm.model";
import { IResultTsmSummary, ResultTsmSummary } from "../../models/reports/tsm-summary.model";
import { IResultCis, ResultCis } from "../../models/reports/cis.model";
import { IResultIma, ResultIma } from "../../models/reports/ima.model";
import { IResultW30, ResultW30 } from "../../models/reports/w30.model";
import {
  GeneralSetting,
  IResultGeneralSetting,
  ResultGeneralSetting
} from "../../models/general/general-setting.model";

@Injectable({
  providedIn: 'root'
})
export class ReportService {

  constructor(private http: ReportHttpService) { }

  filter(
    wheres: { [key: string]: string },
    selectedTab: string,
    limit: number = 100,
    offset: number = 0,
    sort: string
  ): Observable<ResultReportm> {
    return this.http
      .filter(wheres, selectedTab, limit, offset, sort)
      .pipe(map((reportresults: IResultReportm) => ResultReportm.fromObject(reportresults)));
  }

  getSettings(
    wheres: { [key: string]: string },
    limit: number = 100,
    offset: number = 0
  ): Observable<ResultGeneralSetting> {
    return this.http
      .getSettings(wheres, limit, offset)
      .pipe(
        map((data: IResultGeneralSetting) => ResultGeneralSetting.fromObject(data))
      );
  }

  /**
   * Retorna true si el setting "SHOW PATIENT PROFILE" tiene param_value = 'Y'
   */
  getShowPatientProfileEnabled(): Observable<boolean> {
    return this.getSettings({}, 100, 0).pipe(
      map((resp: ResultGeneralSetting) => {
        const setting = resp?.results?.find(
          (x: GeneralSetting) =>
            (x?.paramKey ?? '').toString().trim().toUpperCase() === 'SHOW PATIENT PROFILE'
        );

        return (setting?.paramValue ?? '').toString().trim().toUpperCase() === 'Y';
      })
    );
  }

  /**
   * Retorna true si el setting "SHOW RA SCORECARD" tiene param_value = 'Y'
   */
  getShowRiskAdjustmentScorecardEnabled(): Observable<boolean> {
    return this.getSettings({}, 100, 0).pipe(
      map((resp: ResultGeneralSetting) => {
        const setting = resp?.results?.find(
          (x: GeneralSetting) =>
            (x?.paramKey ?? '').toString().trim().toUpperCase() === 'SHOW RA SCORECARD'
        );

        return (setting?.paramValue ?? '').toString().trim().toUpperCase() === 'Y';
      })
    );
  }

  /**
   * Retorna true si el setting "SHOW PATIENT PROFILE" tiene param_value = 'Y'
   */
  getShowIncentiveProgramEnabled(): Observable<boolean> {
    return this.getSettings({}, 100, 0).pipe(
      map((resp: ResultGeneralSetting) => {
        const setting = resp?.results?.find(
          (x: GeneralSetting) =>
            (x?.paramKey ?? '').toString().trim().toUpperCase() === 'SHOW SIIP'
        );

        return (setting?.paramValue ?? '').toString().trim().toUpperCase() === 'Y';
      })
    );
  }







  /**
   * Método genérico para validar settings tipo flag (Y/N)
   */
  isSettingEnabled(paramKey: string): Observable<boolean> {
    return this.getSettings({}, 100, 0).pipe(
      map((resp: any) => {
        const setting = resp?.results?.find(
          (x: any) =>
            (x?.param_key ?? '').toString().trim().toUpperCase() ===
            (paramKey ?? '').toString().trim().toUpperCase()
        );

        return (setting?.param_value ?? '').toString().trim().toUpperCase() === 'Y';
      })
    );
  }

  closedRA(
    wheres: { [key: string]: string },
    limit: number = 100,
    offset: number = 0,
    sort: string
  ): Observable<ResultClosedRa> {
    return this.http
      .closedRA(wheres, limit, offset, sort)
      .pipe(map((results: IResultClosedRa) => ResultClosedRa.fromObject(results)));
  }

  closedGAP(
    wheres: { [key: string]: string },
    limit: number = 100,
    offset: number = 0,
    sort: string
  ): Observable<ResultClosedGap> {
    return this.http
      .closedGAP(wheres, limit, offset, sort)
      .pipe(map((results: IResultClosedGap) => ResultClosedGap.fromObject(results)));
  }

  SIIPResumen(
    wheres: { [key: string]: string },
    limit: number = 100,
    offset: number = 0,
    sort: string
  ): Observable<ResultBonusResumen> {
    return this.http
      .SIIPResumen(wheres, limit, offset, sort)
      .pipe(map((results: IResultBonusResumen) => ResultBonusResumen.fromObject(results)));
  }

  SIIPDetails(
    wheres: { [key: string]: string },
    limit: number = 100,
    offset: number = 0,
    sort: string
  ): Observable<ResultBonusDetail> {
    return this.http
      .SIIPDetails(wheres, limit, offset, sort)
      .pipe(map((results: IResultBonusDetail) => ResultBonusDetail.fromObject(results)));
  }

  TSMDetails(
    wheres: { [key: string]: string },
    limit: number = 100,
    offset: number = 0,
    sort: string
  ): Observable<ResultTsm> {
    return this.http
      .TSMDetails(wheres, limit, offset, sort)
      .pipe(map((results: IResultTsm) => ResultTsm.fromObject(results)));
  }

  TSMSummary(
    wheres: { [key: string]: string },
    limit: number = 100,
    offset: number = 0,
    sort: string
  ): Observable<ResultTsmSummary> {
    return this.http
      .TSMSummary(wheres, limit, offset, sort)
      .pipe(map((results: IResultTsmSummary) => ResultTsmSummary.fromObject(results)));
  }

  CISDetails(
    wheres: { [key: string]: string },
    limit: number = 100,
    offset: number = 0,
    sort: string
  ): Observable<ResultCis> {
    return this.http
      .CISDetails(wheres, limit, offset, sort)
      .pipe(map((results: IResultCis) => ResultCis.fromObject(results)));
  }

  IMADetails(
    wheres: { [key: string]: string },
    limit: number = 100,
    offset: number = 0,
    sort: string
  ): Observable<ResultIma> {
    return this.http
      .IMADetails(wheres, limit, offset, sort)
      .pipe(map((results: IResultIma) => ResultIma.fromObject(results)));
  }

  W30Details(
    wheres: { [key: string]: string },
    limit: number = 100,
    offset: number = 0,
    sort: string
  ): Observable<ResultW30> {
    return this.http
      .W30Details(wheres, limit, offset, sort)
      .pipe(map((results: IResultW30) => ResultW30.fromObject(results)));
  }

  checkPracticeCapabilities(practiceId: string): Observable<any> {
    return this.http.checkPracticeCapabilities(practiceId);
  }

  getKpiData(practiceId: string, kpiType?: string): Observable<any> {
    return this.http.getKpiData(practiceId, kpiType);
  }
}
